import React from "react";
import { Box, Typography, FormControl, Select, MenuItem, Autocomplete, TextField } from "@mui/material";
import { Icon } from "@iconify/react";
import FormTextField from "../FormControls/FormTextFeilds";
import CenterAddressPicker from "../FormControls/FormAddressPicker";

const BillingDetails = ({
  isSameAsAddress,
  handleSameAsToggle,
  formErrors,
  register,
  validateField,
  watch,
  setValue,
  states,
  cities,
  selectedStateObj,
  isBillingManual,
  setIsBillingManual,
  setBillingAddress,
}) => {

  const selectSx = {
    height: "48px",
    borderRadius: "8px",
    backgroundColor: "var(--app-surface)",

    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid var(--app-border) !important",
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "1px solid var(--app-border)",
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "1px solid var(--app-border)",
    },
    "& .MuiInputBase-input": {
      fontSize: "14px",   // change font size here
      fontWeight: 400,    // optional
    },

    // Normal text
    "& .MuiSelect-select": {
      padding: "12px 14px",
      color: "var(--app-text)",
    },

    // DISABLED ROOT (background + border)
    "&.Mui-disabled": {
      backgroundColor: "var(--app-surface-2)",
      cursor: "not-allowed",
      opacity: 1, // remove fade
    },

    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      border: "1px solid var(--app-border)",
    },

    //  DISABLED TEXT
    "&.Mui-disabled .MuiSelect-select": {
      WebkitTextFillColor: "var(--app-text-muted)", // disabled text color
      color: "var(--app-text-muted)",
    },

  };

  const menuProps = {
    PaperProps: {
      sx: {
        maxHeight: 260,
        mt: 1,
        borderRadius: "8px",
      },
    },
  };
  const DropdownIcon = (props) => (
    <Icon
      icon="eva:arrow-ios-downward-fill"
      width="20"
      height="20"
      {...props} // ← IMPORTANT
    />
  );
  return (
    <Box className="card2 pt-20 mt-20" sx={{ p: 2.5, fontFamily: "Figtree" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontSize={20} fontWeight={700} color="var(--app-text)">
          Billing Details
        </Typography>
      </Box>

      {/* Same as checkbox */}
      <Box mt={1.5} display="flex" alignItems="center" gap={1}>
        <input
          type="checkbox"
          id="sameAsAbove"
          checked={isSameAsAddress}
          onChange={(e) => handleSameAsToggle(e.target.checked)}
        />
        <Typography fontSize={15} fontWeight={500} color="var(--app-text)">Same as organisation details</Typography>
      </Box>

      {/* Form Rows */}
      <Box display="flex" flexWrap="wrap" mt={3} mx={-1}>
        {/* Name */}
        <Box sx={{ px: 1, flex: { xs: "1 1 100%", md: "1 1 50%" } }}>
          <Typography fontSize={14} fontWeight={500} mb="6px" color="var(--app-text)">
            Name <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <FormTextField
            name="orgBillingDetails.name"
            register={register}
            errors={formErrors.orgBillingDetails?.name}
            required
            placeholder="Enter Billing Name"
            disabled={isSameAsAddress}
            onBlur={() => validateField("orgBillingDetails.name")}
          />
        </Box>

        {/* Tax ID */}
        <Box sx={{ px: 1, flex: { xs: "1 1 100%", md: "1 1 50%" } }}>
          <Typography fontSize={14} fontWeight={500} mb="6px" color="var(--app-text)">
            Tax ID <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <FormTextField
            name="orgBillingDetails.taxId"
            register={register}
            errors={formErrors?.orgBillingDetails?.taxId?.message}
            required
            placeholder="Enter Tax ID"
            onBlur={() => validateField("orgBillingDetails.taxId")}
          />
        </Box>

        {/* Pincode */}
        <Box sx={{ px: 1, mt: 2, flex: { xs: "1 1 100%", md: "1 1 33.33%" } }}>
          <Typography fontSize={14} fontWeight={500} mb="6px" color="var(--app-text)">
            Pincode <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <FormTextField
            name="orgBillingDetails.pincode"
            register={register}
            errors={formErrors.orgBillingDetails?.pincode?.message}
            required
            placeholder="Enter Pincode"
            disabled={isSameAsAddress}
            onBlur={() => validateField("orgBillingDetails.pincode")}
          />
        </Box>

        {/* State */}
        <Box sx={{ px: 1, mt: 2, flex: { xs: "1 1 100%", md: "1 1 33.33%" } }}>
          <Typography fontSize={14} fontWeight={500} mb="6px" color="var(--app-text)">
            State <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>

          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={watch("orgBillingDetails.state") || ""}
              disabled={isSameAsAddress}
              onChange={(e) => {
                setValue("orgBillingDetails.state", e.target.value, {
                  shouldDirty: true,
                });
                setValue("orgBillingDetails.city", "", { shouldDirty: true });
                validateField("orgBillingDetails.state");
              }}
              sx={selectSx}
              MenuProps={menuProps}
              IconComponent={DropdownIcon}
            >
              <MenuItem value="" disabled>
                Select State
              </MenuItem>
              {states.map((s) => (
                <MenuItem key={s.iso2} value={s.name}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {formErrors.orgBillingDetails?.state && (
            <Typography color="error" fontSize={12} mt={0.5}>
              {formErrors.orgBillingDetails?.state?.message}
            </Typography>
          )}
        </Box>

        {/* City */}
        {/* City */}
        <Box sx={{ px: 1, mt: 2, flex: { xs: "1 1 100%", md: "1 1 33.33%" } }}>
          <Typography fontSize={14} fontWeight={500} mb="6px" color="var(--app-text)">
            City <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>

          <FormControl fullWidth size="small">
            <Autocomplete
              freeSolo
              autoSelect
              disabled={!selectedStateObj || isSameAsAddress}
              options={cities}
              value={watch("orgBillingDetails.city") || ""}
              onChange={(_, newVal) => {
                setValue("orgBillingDetails.city", newVal || "", {
                  shouldDirty: true,
                });
                validateField("orgBillingDetails.city");
              }}
              onInputChange={(_, newInput, reason) => {
                if (reason === "input") {
                  setValue("orgBillingDetails.city", newInput, {
                    shouldDirty: true,
                  });
                  validateField("orgBillingDetails.city");
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "48px",
                  borderRadius: "8px",

                  "& fieldset": {
                    border: "1px solid var(--app-border)",
                  },

                  "&:hover fieldset": {
                    border: "1px solid var(--app-border)",
                  },

                  "&.Mui-focused fieldset": {
                    border: "1px solid var(--app-border)",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or type city"
                  sx={selectSx}
                />
              )}
            />
          </FormControl>

          {formErrors.orgBillingDetails?.city && (
            <Typography color="error" fontSize={12} mt={0.5}>
              {formErrors.orgBillingDetails?.city?.message}
            </Typography>
          )}
        </Box>
        {/* <Box sx={{ px: 1, mt: 2, flex: { xs: "1 1 100%", md: "1 1 33.33%" } }}>
          <Typography fontSize={14} fontWeight={500} mb="6px" color="var(--app-text)">
            City <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>

          <FormControl fullWidth size="small">
            <Autocomplete
              freeSolo
              autoSelect
              disabled={!selectedStateObj || isSameAsAddress}
              options={cities}
              value={watch("orgBillingDetails.city") || ""}
              onChange={(_, newVal) => {
                setValue("orgBillingDetails.city", newVal || "", {
                  shouldDirty: true,
                });
                validateField("orgBillingDetails.city");
              }}
              onInputChange={(_, newInput, reason) => {
                if (reason === "input") {
                  setValue("orgBillingDetails.city", newInput, {
                    shouldDirty: true,
                  });
                  validateField("orgBillingDetails.city");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or type city"
                  sx={selectSx}
                />
              )}
            />
          </FormControl>

          {formErrors.orgBillingDetails?.city && (
            <Typography color="error" fontSize={12} mt={0.5}>
              {formErrors.orgBillingDetails?.city?.message}
            </Typography>
          )}
        </Box> */}

        {/* Address */}
        <Box sx={{ px: 1, mt: 2, flex: { xs: "1 1 100%", md: "1 1 50%" } }}>
          <Typography fontSize={14} fontWeight={500} mb="6px" color="var(--app-text)">
            Address <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>

          {!isBillingManual ? (
            <Box
              sx={{ pointerEvents: isSameAsAddress ? "none" : "auto", opacity: isSameAsAddress ? 0.6 : 1 }}
            >
              <CenterAddressPicker
                label=""
                required
                value={watch("orgBillingDetails.address.fulladdress")}
                error={formErrors.orgBillingDetails?.address?.fulladdress?.message}
                onChange={(fullDisplayAddress, fullAddressObj) => {
                  setValue(
                    "orgBillingDetails.address.fulladdress",
                    fullDisplayAddress,
                    { shouldDirty: true, shouldValidate: true }
                  );

                  setValue("orgBillingDetails.address", fullAddressObj, {
                    shouldDirty: true,
                  });

                  setValue("orgBillingDetails.pincode", fullAddressObj.pincode, {
                    shouldDirty: true,
                  });
                  setValue("orgBillingDetails.city", fullAddressObj.city, {
                    shouldDirty: true,
                  });
                  setValue("orgBillingDetails.state", fullAddressObj.state, {
                    shouldDirty: true,
                  });

                  validateField("orgBillingDetails.address.fulladdress");
                }}
              />
            </Box>
          ) : (
            <FormTextField
              label=""
              name="orgBillingDetails.address.fulladdress"
              value={watch("orgBillingDetails.address.fulladdress")}
              errors={formErrors.orgBillingDetails?.address?.fulladdress}
              required
              placeholder="Enter Billing Address"
              disabled={isSameAsAddress}
              onBlur={() =>
                validateField("orgBillingDetails.address.fulladdress")
              }
              onChange={(e) =>
                setValue(
                  "orgBillingDetails.address.fulladdress",
                  e.target.value,
                  { shouldDirty: true, shouldValidate: true }
                )
              }
            />
          )}
        </Box>

        {/* Tax State Code */}
        <Box sx={{ px: 1, mt: 2, flex: { xs: "1 1 100%", md: "1 1 50%" } }}>
          <Typography fontSize={14} fontWeight={500} mb="6px" color="var(--app-text)">
            Tax State Code <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <FormTextField
            name="orgBillingDetails.stateCode"
            register={register}
            errors={formErrors.orgBillingDetails?.stateCode?.message}
            required
            placeholder="Enter Tax State Code"
            onBlur={() => validateField("orgBillingDetails.stateCode")}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default BillingDetails;
